/**
 * Single source of truth for a product's weighted total across the rubric.
 *
 * Both the ranking list (/products) and the detail page (/products/$slug) — and
 * the per-product term inside the candidate composite — MUST score a product the
 * same way, otherwise the ranking mis-orders products relative to their own
 * detail pages. The bug this prevents: iterating only stored `product_scores`
 * rows silently drops the derived-coverage criteria (which have no stored row),
 * understating every product with coverage. This helper iterates `criteria`
 * (not stored scores) so manual AND derived-coverage criteria both contribute.
 *
 * Scoring rules (identical to migration 0040 / 0042 intent):
 *   - weight w = weights[slug] ?? weight_default (active preset → default)
 *   - dealbreaker_fail: counted, contributes nothing to the total
 *   - derived-coverage criterion: contributes computeDerivedCoverage(...).score * w
 *     (null derived score = "not assessed" → skipped, never penalised)
 *   - manual criterion: contributes score * w when score is not null
 * Since scores are 0–5 and weights sum to ~1, the total is already on a 0–5 scale.
 */

import { computeDerivedCoverage } from "@/application/use-cases/products/derive-coverage";
import type {
  CriterionGroup,
  EvaluationCriterionRow,
  FeatureProductRow,
  FeatureRoadmapVerdictRow,
  FeatureRow,
  ProductScoreRow,
} from "@/infrastructure/supabase/database.types";

export interface ProductScoreContext {
  criteria: EvaluationCriterionRow[];
  features: FeatureRow[];
  verdicts: FeatureRoadmapVerdictRow[];
  weights: Record<string, number>;
}

/** One criterion's positive contribution to the weighted total (for bars/segments). */
export interface ProductScoreContribution {
  slug: string;
  label: string;
  groupKey: CriterionGroup;
  value: number;
}

export interface ProductScoreResult {
  weightedTotal: number;
  /** Manual + derived criteria that contributed a (non-null) score. */
  scoredCount: number;
  /** Manual criteria carrying evidence_md. */
  withEvidenceCount: number;
  /** Manual criteria flagged vibes_revisit. */
  vibesCount: number;
  /** Names of criteria the product fails as a dealbreaker. */
  dealbreakerNames: string[];
  hasDealbreaker: boolean;
  /** Positive per-criterion contributions, for contribution bars. */
  contributions: ProductScoreContribution[];
}

export function computeProductScore(
  scores: ProductScoreRow[],
  productAssignments: FeatureProductRow[],
  ctx: ProductScoreContext
): ProductScoreResult {
  const scoreById = new Map(scores.map((s) => [s.criterion_id, s]));
  let weightedTotal = 0;
  let scoredCount = 0;
  let withEvidenceCount = 0;
  let vibesCount = 0;
  const dealbreakerNames: string[] = [];
  const contributions: ProductScoreContribution[] = [];

  for (const c of ctx.criteria) {
    const w = ctx.weights[c.slug] ?? c.weight_default;
    const s = scoreById.get(c.id);

    if (s?.dealbreaker_fail) {
      dealbreakerNames.push(c.name);
      continue;
    }

    if (c.derivation_kind === "derived-coverage") {
      const derived = computeDerivedCoverage(c, {
        features: ctx.features,
        productAssignments,
        verdicts: ctx.verdicts,
      });
      if (derived.score !== null) {
        const value = derived.score * w;
        weightedTotal += value;
        scoredCount += 1;
        if (value > 0) {
          contributions.push({ slug: c.slug, label: c.name, groupKey: c.group_key, value });
        }
      }
      continue;
    }

    if (s && s.score !== null) {
      const value = s.score * w;
      weightedTotal += value;
      scoredCount += 1;
      if (s.evidence_md && s.evidence_md.length > 0) withEvidenceCount += 1;
      if (s.vibes_revisit) vibesCount += 1;
      if (value > 0) {
        contributions.push({ slug: c.slug, label: c.name, groupKey: c.group_key, value });
      }
    }
  }

  return {
    weightedTotal,
    scoredCount,
    withEvidenceCount,
    vibesCount,
    dealbreakerNames,
    hasDealbreaker: dealbreakerNames.length > 0,
    contributions,
  };
}
